import React, { useEffect, useState, useCallback } from "react";
import { api, fmtMoney } from "../lib/api";
import { Download, FileText } from "lucide-react";
import { toast } from "sonner";

export default function TaxReport() {
  const [year, setYear] = useState(new Date().getFullYear());
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async (y) => {
    setLoading(true);
    try {
      const { data } = await api.get(`/tax-report/${y}`);
      setReport(data);
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(year); }, [year, load]);

  const exportCsv = async () => {
    try {
      const res = await api.get(`/tax-report/${year}/export`, { responseType: "blob" });
      const blob = new Blob([res.data], { type: "text/csv" });
      const link = document.createElement("a");
      link.href = URL.createObjectURL(blob);
      link.download = `tax_report_${year}.csv`;
      link.click();
      URL.revokeObjectURL(link.href);
      toast.success("Report exported");
    } catch {
      toast.error("Export failed");
    }
  };

  const availableYears = report?.available_years?.length
    ? report.available_years
    : [String(year)];

  return (
    <div className="min-h-screen bg-champagne">
      <div className="border-b border-stone px-8 py-5 sticky top-0 bg-champagne z-10 flex items-center justify-between">
        <div>
          <div className="text-[10px] tracking-[0.3em] uppercase text-paars-dark font-bold">// SYS::TAX_REPORT</div>
          <h1 className="text-2xl font-bold tracking-tight text-graphite">1099-INT Summary</h1>
        </div>
        <div className="flex items-center gap-2">
          <select
            data-testid="year-select"
            value={year}
            onChange={(e)=>setYear(Number(e.target.value))}
            className="bg-white border border-stone px-3 py-2 text-sm font-mono text-graphite focus:border-paars focus:outline-none"
          >
            {availableYears.map((y) => <option key={y} value={y}>{y}</option>)}
          </select>
          <button data-testid="export-tax-btn" onClick={exportCsv} disabled={!report?.by_ticker?.length} className="px-4 py-2.5 bg-paars text-graphite font-semibold text-sm hover:bg-paars-light flex items-center gap-2 disabled:opacity-50">
            <Download className="w-4 h-4" /> EXPORT CSV
          </button>
        </div>
      </div>

      <div className="p-8 space-y-6">
        <div className="border border-stone bg-champagne p-8 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <FileText className="w-10 h-10 text-paars-dark" strokeWidth={1.2} />
            <div>
              <div className="text-[10px] tracking-[0.25em] uppercase text-graphite/55 font-bold">TOTAL INTEREST INCOME · {year}</div>
              <div className="text-4xl font-mono text-groen-dark mt-1" data-testid="tax-total">
                {loading ? "—" : fmtMoney(report?.total_interest_income || 0)}
              </div>
              <div className="text-xs text-graphite/55 mt-1">
                Babybond interest is typically reported on IRS Form 1099-INT, Box 1. Consult your tax advisor.
              </div>
            </div>
          </div>
        </div>

        <div className="border border-stone bg-champagne">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-stone/50 border-b border-stone">
                {["Ticker", "Payments", "Total Income"].map((h, i) => (
                  <th key={h} className={`px-4 py-3 text-[10px] tracking-[0.2em] uppercase font-bold text-graphite/55 ${i > 0 ? "text-right" : "text-left"}`}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {loading && (
                <tr><td colSpan={3} className="p-6 text-center text-graphite/45 font-mono text-xs">LOADING...</td></tr>
              )}
              {!loading && (!report?.by_ticker || report.by_ticker.length === 0) && (
                <tr><td colSpan={3} className="p-12 text-center text-graphite/45 font-mono text-xs">
                  NO DIVIDEND PAYMENTS RECORDED FOR {year}
                </td></tr>
              )}
              {report?.by_ticker?.map((r) => (
                <tr key={r.ticker} data-testid={`tax-row-${r.ticker}`} className="border-b border-stone/60 hover:bg-white">
                  <td className="px-4 py-3 font-mono font-semibold text-paars-dark">{r.ticker}</td>
                  <td className="px-4 py-3 font-mono text-right text-graphite/60">{r.count}</td>
                  <td className="px-4 py-3 font-mono text-right text-groen-dark text-base">{fmtMoney(r.total)}</td>
                </tr>
              ))}
              {report?.by_ticker?.length > 0 && (
                <tr className="border-t-2 border-paars/40 bg-white">
                  <td className="px-4 py-3 text-[10px] tracking-[0.25em] uppercase text-graphite/80 font-bold">TOTAL</td>
                  <td className="px-4 py-3"></td>
                  <td className="px-4 py-3 font-mono text-right text-groen-dark text-lg">{fmtMoney(report.total_interest_income)}</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
